import styled from "@emotion/styled";
import { LocalDate, LocalDateTime, LocalTime, Month } from "@js-joda/core";
import classNames from "classnames";
import * as React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import {
  CaregiverImportedVisitDetails,
  PatientTaskInstance,
  PatientTaskInstanceVisitUnion,
  VisitInstanceId,
} from "../messages";
import { CaregiverId, ImportedVisitId, PatientTaskInstanceId } from "../messages/ObjectIds";
import {
  CaregiverImportedVisitDetailsTaskUnion,
  ClockedInStatus,
  ClockInOutCallType,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../messages/Visit";
import { STRINGS } from "../strings";
import {
  ScheduledVisitDetailsAndRequests,
  ScheduledVisitDetailsAndRequestsTaskUnion,
} from "../system/AppState/AppScheduledVisitsState";
import { globalize, translate } from "../translate";
import { doNothing } from "../utils/doNothing";
import { List } from "../utils/List";
import { CHECK_SVG, NOTES_SVG } from "../utils/svgs";
import { CHEVRON_UP_ICON, DOCUMENT_ICON, MOON_ICON, SUN_ICON } from "../utils/svgs_new";
import { CicoModalEntity } from "../utils/visitUtils";
import { LocalDateMultiMap } from "./AssignedVisitsAndTasksList";
import ClockInOutModals, { useClockInOutModals } from "./ClockInOutModals";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppOverlay } from "./Generic/AppOverlay";
import { If } from "./Generic/If";
import NextButton from "./NextButton/NextButton";
import ScreenPlaceholder from "./ScreenPlaceholder";
import TaskCard from "./TaskCard";
import { UseContactSupportReturnType } from "./useContactSupport";
import VisitEngagementButtons from "./VisitEngagementButtons";
import { isOnDaylight as isInDaylight } from "./VisitScreen/VisitScreenUtils";

export interface CaregiverScheduleScreenProps {
  caregiverId: CaregiverId;
  clockedInStatus: ClockedInStatus;
  scheduledAndImportedVisits: LocalDateMultiMap<
    | ScheduledVisitDetailsAndRequestsTaskUnion
    | CaregiverImportedVisitDetailsTaskUnion
    | PatientTaskInstanceVisitUnion
  >;
  patientTaskInstances: List<PatientTaskInstance>;
  month: Month;
  year: number;
  contactSupportProps: UseContactSupportReturnType;
  isCaregiverOfMedflyt: boolean;
  onRequestNavigate: (page: Page) => void;
  onScheduledVisitClick: (visitInstanceId: VisitInstanceId) => void;
  onImportedVisitClick: (importedVisitId: ImportedVisitId, patientName: string) => void;
  onClickTask: (
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onClickNextMonth: () => void;
  onCheckIfDocumentsSubmitted: (visitId: VisitInstanceId) => Promise<boolean>;
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
}

const CaregiverScheduleScreen = (props: CaregiverScheduleScreenProps) => {
  const pendingTasksRef = React.useRef<HTMLDivElement>(null);
  const pendingTasksNavPinRef = React.useRef<HTMLButtonElement>(null);

  let index = 0;

  const handleClock = async (
    visitInstanceId: VisitInstanceId,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams,
    type: ClockInOutCallType
  ) => {
    const result = await props.onClock(
      visitInstanceId,
      type,
      visitInstanceClockinClockoutRequestParams
    );

    setTimeout(() => {
      const element = document.querySelector("#clockInOut");

      if (element !== null) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);

    return result;
  };

  const handleRequireManualClock = (visitInstanceId: VisitInstanceId) => {
    props.onRequestNavigate({
      type: "TimesheetActivity",
      visitInstanceId: visitInstanceId,
    });
  };

  const handleClickClockIn = (visitDetails: VisitInstanceDetails) =>
    promptClock({
      clockedInStatus: props.clockedInStatus,
      visitInstance: visitDetails,
      clockType: ClockInOutCallType.IN,
    });

  const handleClickClockOut = (visitDetails: VisitInstanceDetails) =>
    promptClock({
      clockedInStatus: props.clockedInStatus,
      visitInstance: visitDetails,
      clockType: ClockInOutCallType.OUT,
    });

  const onClickClockIn = (visitOrTask: PatientTaskInstanceVisitUnion) => {
    if (!("visitInstance" in visitOrTask) || visitOrTask.visitInstance === null) {
      return;
    }

    handleClickClockIn(visitOrTask.visitInstance);
  };

  const onClickClockOut = (visitOrTask: PatientTaskInstanceVisitUnion) => {
    if (!("visitInstance" in visitOrTask) || visitOrTask.visitInstance === null) {
      return;
    }

    handleClickClockOut(visitOrTask.visitInstance);
  };

  const handleClickStartNow = (visitOrTask: PatientTaskInstanceVisitUnion) => {
    if (!("visitInstance" in visitOrTask) || visitOrTask.visitInstance === null) {
      return;
    }

    const entity: CicoModalEntity = {
      type: "task",
      task: { ...visitOrTask, visitInstance: visitOrTask.visitInstance },
    };

    props.onClickStartNow(entity);
  };

  const handleClickFutureVisit = (visitDetails: VisitInstanceDetails) =>
    openFutureVisitModal(visitDetails);

  const {
    state: clockInClockoutModalsState,
    actions: { promptClock, openFutureVisitModal },
  } = useClockInOutModals({
    isCaregiverOfMedflyt: props.isCaregiverOfMedflyt,
    caregiverId: props.caregiverId,
    onClickClock: handleClock,
    onRequireManualClock: handleRequireManualClock,
    onCheckIfDocumentsSubmitted: (visitId) => props.onCheckIfDocumentsSubmitted(visitId),
    onClockPresentVisit: (visit) => props.onClickStartNow({ type: "visit", visit }),
    onUploadVerificationSelfie: props.onUploadVerificationSelfie,
    onRequestVisitAdjustmentCheck: props.onRequestVisitAdjustmentCheck,
    onRequestMissedVisit: props.onCaregiverMarkVisitAsMissed,
  });

  const renderTimeRange = React.useCallback(
    (startsAtDaylight: boolean, localDateTime: LocalDateTime, showIcon: boolean) => {
      return (
        <div className="AssignedItemCard__TimeRange">
          <If condition={showIcon}>
            <div className="AssignedItemCard__TimeRangeIcon">
              {startsAtDaylight ? (
                <AppIcon icon={SUN_ICON} size={16} fill="#FFB932" />
              ) : (
                <AppIcon icon={MOON_ICON} size={16} fill="#283847" />
              )}
            </div>
          </If>

          <div className="AssignedItemCard__TimeRangeText">
            {globalize().formatLocalDateTime(localDateTime, { time: "short" })}
          </div>
        </div>
      );
    },
    []
  );

  const renderImportedVisitTask = React.useCallback(
    (visit: CaregiverImportedVisitDetails) => {
      const IMPORTED_TASK_NAME = "IMPORTED";

      return (
        <AppCard
          key={IMPORTED_TASK_NAME + visit.id}
          className={"CaregiverScheduleScreen_visit"}
          onClick={() => {
            if (visit.agencyMemberId !== null) {
              props.onImportedVisitClick.call(null, visit.id, visit.patientName);
            }
          }}
        >
          <AppCardContent>
            <div className="AssignedItemCard__Headline">
              <h3>{visit.patientName}</h3>
              <If condition={visit.agencyMemberId !== null}>
                <NextButton />
              </If>
            </div>

            <div className="AssignedItemCard__TimeRanges">
              {renderTimeRange(false, visit.startTimeLocal, false)}
              <span> - </span>
              {renderTimeRange(false, visit.endTimeLocal, false)}
            </div>
          </AppCardContent>

          <AppCardContent className="AssignedItemCard__Notes">
            <div className="AssignedItemCard__Note">
              <span className="Leading">{NOTES_SVG}</span>
              <span className="Title">{translate(STRINGS.IMPORTED)}</span>
            </div>
          </AppCardContent>
        </AppCard>
      );
    },
    [props.onImportedVisitClick, renderTimeRange]
  );

  const renderVisitDetails = (
    scheduledVisit: ScheduledVisitDetailsAndRequests,
    itemIndex: number
  ) => {
    const visitDetails = scheduledVisit.scheduledVisitDetails;
    const geo = visitDetails.publicVisitDetails.location.location;
    const doesStartAtDaylight = isInDaylight(visitDetails.startTimeUTC, geo);
    const doesEndAtDaylight = isInDaylight(visitDetails.endTimeUTC, geo);

    return (
      <AppCard
        key={scheduledVisit.scheduledVisitDetails.id.toString()}
        className="CaregiverScheduleScreen_visit"
        style={{ animationDelay: `${Math.min(itemIndex * 75, 500)}ms` }}
      >
        <AppCardContent onClick={() => props.onScheduledVisitClick.call(null, visitDetails.id)}>
          <div className="AssignedItemCard__Headline">
            <h3>{visitDetails.confidentialVisitDetails.patientName}</h3>
            <NextButton />
          </div>
          <div className="AssignedItemCard__TimeRanges">
            {renderTimeRange(doesStartAtDaylight, visitDetails.startTimeLocal, true)}
            <span> - </span>
            {renderTimeRange(doesEndAtDaylight, visitDetails.endTimeLocal, false)}
          </div>
        </AppCardContent>
        <If condition={visitDetails.isCaregiverClockEnabled}>
          <AppCardContent className="AssignedItemCard__ClockButtons">
            <VisitEngagementButtons
              visitDetails={visitDetails}
              onClickClockIn={handleClickClockIn}
              onClickClockOut={handleClickClockOut}
              onClickStartNow={(visit) => props.onClickStartNow({ type: "visit", visit })}
              onClickFutureVisit={handleClickFutureVisit}
              onClickSubmitTimeSheet={handleRequireManualClock}
            />
          </AppCardContent>
        </If>
        <If condition={visitDetails.visitDocumentsStatus.total > 0}>
          <AppCardContent className="AssignedItemCard__Notes">
            <div
              className={classNames("AssignedItemCard__Note", {
                Completed:
                  visitDetails.visitDocumentsStatus.submitted ===
                  visitDetails.visitDocumentsStatus.total,
              })}
            >
              <span className="Leading">
                {visitDetails.visitDocumentsStatus.submitted ===
                visitDetails.visitDocumentsStatus.total ? (
                  <AppIcon icon={CHECK_SVG} size={12} fill="green" />
                ) : (
                  <AppIcon icon={DOCUMENT_ICON} size={12} fill="#B3B2B8" />
                )}
              </span>
              <span className="Title">
                {visitDetails.visitDocumentsStatus.submitted ===
                visitDetails.visitDocumentsStatus.total
                  ? visitDetails.visitDocumentsStatus.total +
                    " " +
                    translate(STRINGS.SUBMITTED_DOCUMENTS)
                  : visitDetails.visitDocumentsStatus.submitted +
                    " / " +
                    visitDetails.visitDocumentsStatus.total +
                    " " +
                    translate(STRINGS.COMPLETED_DOCUMENTS)}
              </span>
            </div>
          </AppCardContent>
        </If>
      </AppCard>
    );
  };

  const renderSingleVisit = (
    scheduledOrImportedVisit: ScheduledVisitDetailsAndRequests | CaregiverImportedVisitDetails,
    itemIndex: number
  ) => {
    // Check if imported visits
    if ((scheduledOrImportedVisit as any).scheduledVisitDetails === undefined) {
      const importedVisit: CaregiverImportedVisitDetails =
        scheduledOrImportedVisit as CaregiverImportedVisitDetails;
      return renderImportedVisitTask(importedVisit);
    } else {
      const scheduledVisit: ScheduledVisitDetailsAndRequests =
        scheduledOrImportedVisit as ScheduledVisitDetailsAndRequests;

      return renderVisitDetails(scheduledVisit, itemIndex);
    }
  };

  const isPendingTask = React.useCallback(
    (task: PatientTaskInstance): task is PatientTaskInstance.PENDING => {
      const startDateMonth = task.startDate.month().value();
      const startDateYear = task.startDate.year();
      const dueDateGracePeriod = task.dueDate.plusMonths(12);
      const dueDateMonth = dueDateGracePeriod.month().value();
      const dueDateYear = dueDateGracePeriod.year();
      return (
        task.type === "PENDING" &&
        (startDateYear < props.year ||
          (startDateYear === props.year && startDateMonth <= props.month.value())) &&
        (dueDateYear > props.year ||
          (dueDateYear === props.year && dueDateMonth >= props.month.value()))
      );
    },
    [props.month, props.year]
  );

  const getPendingTasks = React.useCallback((): List<PatientTaskInstance.PENDING> => {
    return props.patientTaskInstances.strictFilter(isPendingTask);
  }, [isPendingTask, props.patientTaskInstances]);

  const handleClickShowPendingTasks = React.useCallback(() => {
    if (pendingTasksRef.current !== null) {
      pendingTasksRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, []);

  const pendingTasksObserverCallback = React.useCallback((entries) => {
    if (pendingTasksNavPinRef.current === null) {
      return;
    }

    if (entries[0].intersectionRatio === 0) {
      if (!pendingTasksNavPinRef.current.classList.contains("__visible")) {
        pendingTasksNavPinRef.current.classList.add("__visible");
      }
    } else {
      if (pendingTasksNavPinRef.current.classList.contains("__visible")) {
        pendingTasksNavPinRef.current.classList.remove("__visible");
      }
    }
  }, []);

  React.useEffect(() => {
    const pendingTasksObserverOptions = {
      root: document.querySelector("#VisitsScreen__contentCont"),
      rootMargin: "0px",
      threshold: [0, 0.1],
    };

    if (pendingTasksRef.current === null) {
      return doNothing;
    }

    const pendingTasksObserver = new IntersectionObserver(
      pendingTasksObserverCallback,
      pendingTasksObserverOptions
    );
    pendingTasksObserver.observe(pendingTasksRef.current);
    return () => pendingTasksObserver.disconnect();
  }, [pendingTasksObserverCallback]);

  const renderedDaysWithVisits = (() => {
    const daysWithVisitsElements: React.ReactNode[] = [];
    const pendingTasks = getPendingTasks();

    if (pendingTasks.length() > 0) {
      daysWithVisitsElements.push(
        <div
          ref={pendingTasksRef}
          key="pendingTasks"
          className={classNames({
            CaregiverScheduleScreen__dateContainer: pendingTasks.length() > 0,
          })}
        >
          <div className={"CaregiverScheduleScreen_visitsContainer"}>
            {pendingTasks.map((pendingTask) => {
              index++;

              return (
                <TaskCard
                  key={"pendingTask" + index}
                  style={{ animationDelay: `${Math.min(index * 75, 500)}ms` }}
                  task={pendingTask}
                  onClick={props.onClickTask}
                  clockedInStatus={props.clockedInStatus}
                />
              );
            })}
          </div>
        </div>
      );
    }

    function sortByCaregiverEventStartTime(
      val: Array<
        | PatientTaskInstanceVisitUnion
        | ScheduledVisitDetailsAndRequestsTaskUnion
        | CaregiverImportedVisitDetailsTaskUnion
      >
    ) {
      function getStartTime(
        val:
          | PatientTaskInstanceVisitUnion
          | ScheduledVisitDetailsAndRequestsTaskUnion
          | CaregiverImportedVisitDetailsTaskUnion
      ) {
        if (val.union_type === "SCHEDULED_VISIT") {
          return LocalDateTime.from(val.scheduledVisitDetails.startTimeLocal);
        } else if (val.union_type === "IMPORTED_VISIT") {
          return LocalDateTime.from(val.startTimeLocal);
        } else if (val.scheduleDateTime) {
          return LocalDateTime.ofInstant(val.scheduleDateTime);
        } else {
          return LocalDateTime.of(val.startDate, LocalTime.MIN);
        }
      }

      return val.sort((a, b) => {
        const timeA = getStartTime(a);
        const timeB = getStartTime(b);
        return timeA.compareTo(timeB);
      });
    }

    props.scheduledAndImportedVisits.forEach((val, key: LocalDate) => {
      const isCurrentDay = key.equals(LocalDate.now());
      if (key.month() === props.month && key.year() === props.year) {
        const sortedVisitsAndTasks = sortByCaregiverEventStartTime(val);

        daysWithVisitsElements.push(
          <div
            className={"CaregiverScheduleScreen__dateContainer"}
            key={key.toString()}
            id={"day" + key.toString()}
          >
            <div className="CaregiverScheduleScreen__visitDate">
              <If condition={isCurrentDay}>
                <span className="CurrentDay">{translate(STRINGS.TODAY)}</span>
              </If>
              <span className="DayOfWeek">
                {globalize().formatDayOfWeek(key.dayOfWeek(), true)}
              </span>
              <span className="DayOfMonth">{key.dayOfMonth()}</span>
            </div>
            <div className={"CaregiverScheduleScreen_visitsContainer"}>
              {sortedVisitsAndTasks.map((visitOrTask) => {
                index++;
                switch (visitOrTask.union_type) {
                  case "IMPORTED_VISIT":
                  case "SCHEDULED_VISIT":
                    return renderSingleVisit(visitOrTask, index);
                  case "TASK":
                    return (
                      <TaskCard
                        key={"task" + index}
                        style={{
                          animationDelay: `${Math.min(index * 75, 500)}ms`,
                        }}
                        task={visitOrTask}
                        clockedInStatus={props.clockedInStatus}
                        onClick={props.onClickTask}
                        onClickClockIn={() => onClickClockIn(visitOrTask)}
                        onClickClockOut={() => onClickClockOut(visitOrTask)}
                        onClickStartNow={() => handleClickStartNow(visitOrTask)}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </div>
        );
      }
    });

    return daysWithVisitsElements;
  })();

  const renderedPendingTasksNavPin = React.useMemo(() => {
    const pendingTasksAmount = getPendingTasks().length();
    if (pendingTasksAmount === 0) {
      return null;
    }

    return (
      <AppButton
        ref={pendingTasksNavPinRef}
        className="CaregiverScheduleScreen_PendingTasksNavPin"
        size="small"
        onClick={handleClickShowPendingTasks}
      >
        <span>{`${translate(
          STRINGS.VIEW_TASKS_PENDING_FOR_SCHEDULE
        )} (${pendingTasksAmount})`}</span>
        <AppIcon icon={CHEVRON_UP_ICON} fill="white" size={10} />
      </AppButton>
    );
  }, [getPendingTasks, handleClickShowPendingTasks]);

  const visitsNextMonth = React.useMemo(() => {
    const firstDayOfMonth = LocalDate.of(props.year, props.month, 1);
    const firstDayOfNextMonth = firstDayOfMonth.plusMonths(1);
    return props.scheduledAndImportedVisits.getBetween(
      firstDayOfNextMonth,
      firstDayOfNextMonth.plusMonths(1).minusDays(1)
    );
  }, [props.month, props.scheduledAndImportedVisits, props.year]);

  if (renderedDaysWithVisits.length === 0) {
    return (
      <ScreenPlaceholder
        headline={translate(STRINGS.THERE_SEEMS_TO_BE_NOTHING_HERE)}
        illustration="NoAssignedCases"
      />
    );
  }

  return (
    <div>
      {renderedPendingTasksNavPin}
      <div className="CaregiverScheduleScreen_DaysWithVisits">
        {renderedDaysWithVisits}
        {visitsNextMonth.length > 0 && (
          <VisitsNextMonthsButtonWrapper>
            <div>
              <AppButton onClick={props.onClickNextMonth}>
                {visitsNextMonth.length === 1
                  ? `1 ${translate(STRINGS.VISIT_NEXT_MONTH)}`
                  : `${visitsNextMonth.length} ${translate(STRINGS.VISITS_NEXT_MONTH)}`}
              </AppButton>
            </div>
          </VisitsNextMonthsButtonWrapper>
        )}
      </div>
      <AppOverlay>
        <ClockInOutModals
          contactSupportProps={props.contactSupportProps}
          {...clockInClockoutModalsState}
        />
      </AppOverlay>
    </div>
  );
};

const VisitsNextMonthsButtonWrapper = styled.div`
  padding: 24px 16px;

  > div {
    margin-inline-start: 56px;
    display: flex;

    .AppButton {
      flex: 1;
    }
  }
`;

export default React.memo(CaregiverScheduleScreen);
